import * as React from "react";
import clsx from "clsx";
import { isEqual, set, isSameDay, isBefore, isAfter, format } from "date-fns";
import memoize from "memoize-one";
import { UI, SelectionState, DayFlag, DayPicker } from "react-day-picker";
import { Button } from "../../button/button.mjs";
import { useLabels } from "../../helpers/label-provider/use-labels.mjs";
import { PropsChecker } from "../../helpers/props-checker/props-checker.mjs";
import { useTheme } from "../../helpers/theme-provider/use-theme.mjs";
import { useFormat, isDateRange, isEqualRange, getDateFromValue, getDefaultViewDate, getRange, getValueFromInputValue, getRangesFromTimes } from "./datepicker-helpers.mjs";
import { getDate, isEqualValue } from "./datepicker-helpers.mjs";
import { Dropdown } from "./dropdown.mjs";
import { DropdownNav } from "./dropdown-nav.mjs";
import { TimeColumn } from "./time-column.mjs";
const Datepicker = (props) => {
  var _a;
  const labels = useLabels();
  const { locale: globalLocale } = useTheme();
  const locale = (_a = props.locale) != null ? _a : globalLocale;
  const { format: format2 } = useFormat(locale);
  return /* @__PURE__ */ React.createElement(PropsChecker, { componentProps: props, componentName: "Datepicker", defaultValueName: "defaultValue", valueName: "value", onChangeNames: "onChange" }, /* @__PURE__ */ React.createElement(DatepickerInner, { ...props, locale, formatFn: format2, labels }));
};
const _DatepickerInner = class _DatepickerInner extends React.PureComponent {
  constructor(props) {
    super(props);
    this.cachedViewDate = memoize(_DatepickerInner.getViewDateFromPropsAndState);
    this.cachedRange = memoize(_DatepickerInner.getRangesFromProps);
    this.cachedValue = memoize(_DatepickerInner.getValueFromPropsAndState);
    this.cachedTimes = memoize(_DatepickerInner.getTimesFromProps);
    this.componentCreationDate = /* @__PURE__ */ new Date();
    this.onSelectRange = (value2) => {
      const existingValueIsDifferent = !isDateRange(this.value) || isDateRange(this.value) && !isEqualRange(value2, this.value);
      if (!this.value || existingValueIsDifferent) {
        this.select(value2);
      }
    };
    this.onSelectNonRange = (value2) => {
      const existingValueIsDifferent = isDateRange(this.value) && !isEqual(this.value.from, value2);
      if (!this.value || existingValueIsDifferent) {
        const matchingTimeDate = this.getMatchingTimeDate(value2);
        if (matchingTimeDate) {
          this.select(matchingTimeDate);
        }
      }
    };
    this.onSelect = (value2, precision) => {
      if (!value2) {
        return;
      }
      if (!this.isTimeColumnShown || this.dateLimiter !== "times" && precision !== "minute") {
        this.select(value2);
        return;
      }
      if (isDateRange(value2)) {
        this.onSelectRange(value2);
      } else {
        this.onSelectNonRange(value2);
      }
    };
    this.onClear = () => {
      if (this.stateType === "internal") {
        this.setState({ value: null });
      }
      if (this.props.onClear) {
        this.props.onClear();
      }
    };
    this.onSubmit = () => {
      if (this.props.onSubmit) {
        this.props.onSubmit(this.toOutputValue(this.value));
      }
    };
    this.handleMonthChange = (month) => {
      this.setState({ viewMonth: month });
    };
    this.handleDateSelect = (date) => {
      if (date) {
        this.onSelect(date, "day");
      }
    };
    const value = _DatepickerInner.getInitialValue(props);
    this.state = {
      value,
      viewMonth: getDateFromValue(value, this.componentCreationDate)
    };
  }
  get viewDate() {
    return this.cachedViewDate(this.props, this.state, this.componentCreationDate, this.dateLimiter);
  }
  get dateLimiter() {
    if (this.props.dateLimiter) {
      return this.props.dateLimiter;
    }
    if (this.times) {
      return "times";
    }
    return "range";
  }
  static getViewDateFromPropsAndState(props, state, componentCreationDate, dateLimiter) {
    const value = _DatepickerInner.getValueFromPropsAndState(props, state);
    const times = _DatepickerInner.getTimesFromProps(props);
    if (times && dateLimiter === "times" && !value) {
      let viewDate = getDefaultViewDate(value, times, componentCreationDate);
      viewDate = set(viewDate, { seconds: 0, milliseconds: 0 });
      return viewDate;
    }
    return getDateFromValue(value, componentCreationDate);
  }
  isTimeColumnShown(times) {
    if (!times) {
      return false;
    }
    return !!times.options && !!times.options.length || !!times.helper;
  }
  get range() {
    return this.cachedRange(this.props, this.componentCreationDate, this.dateLimiter);
  }
  static getRangesFromProps(props, componentCreationDate, dateLimiter) {
    return getRange(
      props,
      dateLimiter === "times" ? _DatepickerInner.getTimesFromProps(props) : void 0,
      componentCreationDate,
      dateLimiter === "times" ? _DatepickerInner.isTimeIncludedFromProps(props) : false
    );
  }
  get value() {
    return this.cachedValue(this.props, this.state);
  }
  static getValueFromPropsAndState(props, state) {
    if (_DatepickerInner.getStateTypeFromProps(props) === "internal") {
      return state.value;
    } else {
      return _DatepickerInner.getValueFromProps(props);
    }
  }
  static getValueFromProps(props) {
    return props.value ? getValueFromInputValue(props.value, _DatepickerInner.isTimeIncludedFromProps(props)) : null;
  }
  static getStateTypeFromProps(props) {
    if (props.value !== void 0 && props.onChange !== void 0) {
      return "external";
    }
    return "internal";
  }
  get stateType() {
    return _DatepickerInner.getStateTypeFromProps(this.props);
  }
  get times() {
    return this.cachedTimes(this.props);
  }
  static getTimesFromProps(props) {
    var _a;
    return ((_a = props.times) == null ? void 0 : _a.options) && getRangesFromTimes(props.times.options, _DatepickerInner.isTimeIncludedFromProps(props));
  }
  static isTimeIncludedFromProps(props) {
    return !!props.times;
  }
  select(value) {
    if (this.stateType === "internal") {
      this.setState({ value });
    }
    if (this.props.onChange) {
      this.props.onChange(this.toOutputValue(value));
    }
  }
  toOutputValue(value) {
    var _a;
    const { times } = this.props;
    if (isDateRange(value) && (times == null ? void 0 : times.options)) {
      return (_a = times.options.find((time) => isEqualRange(time, value))) != null ? _a : null;
    }
    return value;
  }
  getMatchingTimeDate(value) {
    if (this.times) {
      for (const time of this.times) {
        if (isSameDay(time.from, value)) {
          return time;
        }
      }
    }
  }
  isInRange(date) {
    if (this.times && this.dateLimiter === "times") {
      for (let index = 0; index < this.times.length; index++) {
        const time = this.times[index];
        const previousTime = this.times[index - 1];
        if (isBefore(date, time.from) && !isSameDay(date, time.from) && isAfter(date, previousTime.to || previousTime.from) && !isSameDay(date, previousTime.to || previousTime.from)) {
          return false;
        }
      }
    }
    return (isAfter(date, this.range.from) || isSameDay(date, this.range.from)) && isBefore(date, this.range.to) || isSameDay(date, this.range.to);
  }
  getSelectedDate() {
    if (this.value) {
      if (isDateRange(this.value)) {
        return this.value.from;
      }
      return this.value;
    }
    if (this.isTimeColumnShown(this.props.times) && this.dateLimiter === "times") {
      return this.viewDate;
    }
  }
  get dayPickerComponents() {
    return {
      Dropdown,
      DropdownNav
    };
  }
  get dayPickerFormatters() {
    return {
      formatWeekNumberHeader: () => "#",
      formatWeekNumber: (weekNumber) => "".concat(weekNumber.toLocaleString())
    };
  }
  get dayPickerClassNames() {
    return {
      [UI.Root]: "datepicker__calendar",
      [UI.Month]: "datepicker__month",
      [UI.MonthCaption]: "datepicker__month-caption",
      [UI.Weekdays]: "datepicker__day-names",
      [UI.Weekday]: "datepicker__day-name",
      [UI.WeekNumberHeader]: "datepicker__week-header",
      [UI.WeekNumber]: "datepicker__week-number",
      [UI.Day]: "datepicker__day",
      [UI.DayButton]: "datepicker__day-inner",
      [SelectionState.selected]: "is-selected",
      [DayFlag.disabled]: "is-disabled",
      [DayFlag.today]: "is-current"
    };
  }
  get dayPickerDisabled() {
    const disabledRange = {
      before: this.range.from,
      after: this.range.to
    };
    if (this.isTimeColumnShown(this.props.times)) {
      return [disabledRange, (date) => !this.isInRange(date)];
    }
    return disabledRange;
  }
  get dayPickerLabels() {
    const { labels, locale } = this.props;
    return {
      labelDayButton: (date, { today, selected }) => {
        let label = format(date, "PPPP", { locale });
        if (today) {
          label = "".concat(labels.get("datepicker.today"), ", ").concat(label);
        }
        if (selected) {
          label = "".concat(label, ", ").concat(labels.get("datepicker.selected"));
        }
        return label;
      },
      labelMonthDropdown: () => labels.get("datepicker.monthLabel"),
      labelYearDropdown: () => labels.get("datepicker.yearLabel"),
      labelWeekNumber: (weekNumber) => labels.get("datepicker.weekNumber", { weekNumber: weekNumber.toString() }),
      labelWeekNumberHeader: () => labels.get("datepicker.weekNumberHeader")
    };
  }
  renderCalendarPicker() {
    return /* @__PURE__ */ React.createElement(
      DayPicker,
      {
        mode: "single",
        locale: this.props.locale,
        weekStartsOn: 1,
        startMonth: this.range.from,
        endMonth: this.range.to,
        month: this.state.viewMonth,
        onMonthChange: this.handleMonthChange,
        disabled: this.dayPickerDisabled,
        selected: this.getSelectedDate(),
        onSelect: this.handleDateSelect,
        hideNavigation: true,
        captionLayout: "dropdown",
        showWeekNumber: this.props.showWeekNumbers,
        components: this.dayPickerComponents,
        formatters: this.dayPickerFormatters,
        classNames: this.dayPickerClassNames,
        labels: this.dayPickerLabels
      }
    );
  }
  renderTimePicker(times) {
    const { formatFn } = this.props;
    const { placeholder, helper, options } = times;
    return /* @__PURE__ */ React.createElement(
      TimeColumn,
      {
        options,
        helper,
        placeholder,
        value: this.value,
        formatFn,
        viewDate: this.viewDate,
        onSelect: this.onSelect
      }
    );
  }
  renderClearButton() {
    const { clearButtonLabel, labels } = this.props;
    return /* @__PURE__ */ React.createElement(Button, { size: "small", variant: "secondary", onClick: this.onClear, text: clearButtonLabel != null ? clearButtonLabel : labels.get("datepicker.clearButtonLabel") });
  }
  renderSubmitButton() {
    const { submitButtonLabel, labels } = this.props;
    return /* @__PURE__ */ React.createElement(Button, { size: "small", onClick: this.onSubmit, text: submitButtonLabel != null ? submitButtonLabel : labels.get("datepicker.submitButtonLabel") });
  }
  render() {
    const { className, times } = this.props;
    return /* @__PURE__ */ React.createElement("div", { className: clsx("datepicker", className) }, /* @__PURE__ */ React.createElement("div", { className: "datepicker__content" }, this.renderCalendarPicker(), this.isTimeColumnShown(times) && this.renderTimePicker(times)), /* @__PURE__ */ React.createElement("div", { className: "datepicker__buttons" }, this.renderSubmitButton(), this.renderClearButton()));
  }
};
_DatepickerInner.getInitialValue = (props) => {
  var _a;
  const stateType = _DatepickerInner.getStateTypeFromProps(props);
  if (stateType === "internal") {
    return props.defaultValue ? getValueFromInputValue(props.defaultValue, _DatepickerInner.isTimeIncludedFromProps(props)) : null;
  } else {
    return (_a = props.value) != null ? _a : null;
  }
};
let DatepickerInner = _DatepickerInner;
export {
  Datepicker,
  getDate,
  getDefaultViewDate,
  getRange,
  getValueFromInputValue,
  isDateRange,
  isEqualRange,
  isEqualValue,
  useFormat
};
